import * as Sentry from "@sentry/react";
import { logToSentry } from "./logger";

// One presence channel per origin: every live JS context of the kiosk app — INCLUDING invisible
// "ghost" webviews leaked by the kiosk shell (see fridge 8ba1c2b5: 4–6 concurrent instances logging
// in lockstep) — shares this channel, because ghosts live in the same app/data store. Purely
// event-driven, per the no-polling rule: announce on birth, farewell on pagehide, no heartbeats.
const CHANNEL_NAME = "eatsmart-instance-presence";

type PresenceMessage = {
	// hello = a newborn instance announcing itself; welcome = an existing instance answering a hello
	// (so the newcomer learns about its elders); bye = an instance unloading (pagehide).
	type: "hello" | "welcome" | "bye";
	id: string;
};

const isPresenceMessage = (value: unknown): value is PresenceMessage => {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	const { type, id } = value as { type?: unknown; id?: unknown };
	return (type === "hello" || type === "welcome" || type === "bye") && typeof id === "string" && id.length > 0;
};

const makeInstanceId = (): string =>
	typeof crypto !== "undefined" && "randomUUID" in crypto
		? crypto.randomUUID()
		: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

let installed = false;

/**
 * Detector-only (no behavior change): counts sibling app instances of the same origin and surfaces
 * them in Sentry, so the fleet's ghost-webview population is measurable with one query.
 *
 * Tags on every event of this pageload:
 *   - instanceId          — short random id of THIS instance (distinguishes siblings' event streams)
 *   - duplicateInstances  — how many OTHER live instances this one currently knows of (0 = healthy)
 * Plus ONE warning event per pageload the first time a sibling is seen.
 *
 * Known limit (accepted to stay passive): a sibling killed without pagehide (jetsam) lingers in the
 * count until our own reload resets it — counts are an upper bound between reloads.
 */
export const installDuplicateInstanceDetector = (): void => {
	if (installed || typeof BroadcastChannel === "undefined") {
		return;
	}
	installed = true;

	const instanceId = makeInstanceId();
	const peers = new Set<string>();
	let hasLoggedDuplicate = false;

	// Full UUID is overkill in the UI; 8 hex chars is plenty to tell 2–6 siblings apart.
	Sentry.setTag("instanceId", instanceId.slice(0, 8));

	const channel = new BroadcastChannel(CHANNEL_NAME);
	const syncPeerTags = () => {
		Sentry.setTag("duplicateInstances", String(peers.size));
		// The ids, not just the count, so two sibling event streams are linkable from tags alone.
		// 8-char ids keep the value far under Sentry's ~200-char tag cap; undefined REMOVES the tag
		// when alone, so healthy kiosks are filterable as `!has:peerInstanceIds`.
		Sentry.setTag("peerInstanceIds", peers.size > 0 ? [...peers].map((id) => id.slice(0, 8)).join(",") : undefined);
	};
	syncPeerTags();

	channel.addEventListener("message", (event: MessageEvent) => {
		const message: unknown = event.data;
		if (!isPresenceMessage(message) || message.id === instanceId) {
			return;
		}
		if (message.type === "bye") {
			peers.delete(message.id);
			syncPeerTags();
			return;
		}
		const isNewPeer = !peers.has(message.id);
		peers.add(message.id);
		if (message.type === "hello") {
			// Answer so the newcomer learns about us — without this, only elders would know the truth.
			channel.postMessage({ type: "welcome", id: instanceId });
		}
		syncPeerTags();
		if (isNewPeer && !hasLoggedDuplicate) {
			hasLoggedDuplicate = true;
			// Once per pageload; the duplicateInstances tag carries the live magnitude from here on.
			logToSentry(
				`Duplicate app instance detected — another live instance of this kiosk shares the origin (peers now: ${peers.size})`,
				"warning",
			);
		}
	});

	// pagehide (not beforeunload) is the reliable teardown signal on iOS WKWebView. A PERSISTED
	// pagehide is bfcache — the page stays alive and this channel stays open, so saying bye would
	// make us invisible to peers while still running. (Kiosks never navigate away, so this branch
	// is theoretical — guarded so a future navigation can't silently break the census.)
	window.addEventListener("pagehide", (event) => {
		if (event.persisted) {
			return;
		}
		channel.postMessage({ type: "bye", id: instanceId });
	});

	channel.postMessage({ type: "hello", id: instanceId });
};
